/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, TextBox, useTheme } from '@veneer/core';
import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';

import { PlainButton } from '../common/styled';
import { StyledModal, TextGroup } from './styled';

import {
  CategoryType,
  Device,
  DeviceType,
  TelemetryFormat
} from '../../types/devices';
import { PostDeviceTelemetryPayload } from '../../types/device-telemetry';
import { OperationStatus } from '../../types';

import { isPCUUID, isSerialNumber } from '../../utils/common';
import { URLS } from '../../utils/urls';
import {
  isCachedCategory,
  updateCachedCategory
} from '../../utils/cached-category';

import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import {
  postDeviceTelemetryCleanup,
  postDeviceTelemetryRequest
} from '../../features/telemetry/device-telemetry-slice';
import { postDeviceMappingRequest } from '../../features/devices/devices-slice';
import usePostTelemetryToast from '../../hooks/usePostTelemetryToast';
import { RootState } from '../../store';

interface RequestDataModalProps {
  deviceDetails: { serialNumber: string; biosUuid: string };
  open: boolean;
  setDeviceDetails: (details: {
    serialNumber: string;
    biosUuid: string;
  }) => void;
  openModal: (value: boolean) => void;
}

const RequestDataModal: React.FC<RequestDataModalProps> = (props) => {
  const { deviceDetails, open, setDeviceDetails, openModal } = props;

  const releasePlatformHpAnalytics = useFeatureFlagContext();

  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    translate,
    northboundAPIs: {
      v1: {
        localization: { country, language }
      }
    }
  } = useDependencyManagerContext();

  const [inputError, setInputError] = useState({
    serialNumber: false,
    biosUuid: false
  });

  const hasStartedTyping = useRef({
    serialNumber: false,
    biosUuid: false
  });

  const { postDeviceTelemetry } = useSelector((state: RootState) => ({
    postDeviceTelemetry: state.DeviceTelemetryReducer?.postDeviceTelemetry?.[
      deviceDetails.serialNumber
    ] || {
      status: OperationStatus.NOT_STARTED
    }
  }));

  const { user } = useSelector((state: RootState) => state.UserReducer);
  const userEmail = user?.email?.value;

  const device: Device = useMemo(
    () => ({
      device_type: DeviceType.PERSONAL_COMPUTER,
      device_serial_nr: deviceDetails.serialNumber,
      device_uuid: deviceDetails.biosUuid,
      device_telemetry_format: TelemetryFormat.CDM
    }),
    [deviceDetails.serialNumber, deviceDetails.biosUuid]
  );

  usePostTelemetryToast(postDeviceTelemetry, device);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const { serialNumber, biosUuid } = deviceDetails;

      setInputError({
        serialNumber:
          hasStartedTyping.current.serialNumber &&
          !isSerialNumber(serialNumber),
        biosUuid: hasStartedTyping.current.biosUuid && !isPCUUID(biosUuid)
      });
    }, 500);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [deviceDetails]);

  useEffect(() => {
    if (!open) {
      hasStartedTyping.current = {
        serialNumber: false,
        biosUuid: false
      };
      setInputError({
        serialNumber: false,
        biosUuid: false
      });
    }
    return () => {
      dispatch(postDeviceTelemetryCleanup());
    };
  }, [open]);

  useEffect(() => {
    if (
      postDeviceTelemetry.status === OperationStatus.COMPLETED ||
      postDeviceTelemetry.status === OperationStatus.ERRORED
    ) {
      setDeviceDetails({ serialNumber: '', biosUuid: '' });
      openModal(false);
    }

    if (postDeviceTelemetry.status === OperationStatus.COMPLETED) {
      if (isCachedCategory(CategoryType.PERSONAL_SYSTEMS, userEmail)) {
        const payload = {
          device_type: DeviceType.PERSONAL_COMPUTER
        };
        dispatch(
          postDeviceMappingRequest({
            type: CategoryType.PERSONAL_SYSTEMS,
            payload
          })
        );
      } else {
        updateCachedCategory(
          CategoryType.PERSONAL_SYSTEMS,
          userEmail,
          Date.now()
        );
      }
    }
  }, [postDeviceTelemetry]);

  const handleChange = (
    details: {
      serialNumber: string;
      biosUuid: string;
    },
    fieldName?: 'serialNumber' | 'biosUuid'
  ) => {
    if (fieldName) {
      hasStartedTyping.current[fieldName] = true;
    }
    setDeviceDetails(details);
  };

  const handlePCRequestTelemetryData = (): void => {
    hasStartedTyping.current.biosUuid = true;
    hasStartedTyping.current.serialNumber = true;

    if (!deviceDetails.serialNumber || !deviceDetails.biosUuid) {
      setInputError({
        serialNumber:
          deviceDetails.serialNumber === '' ||
          !isSerialNumber(deviceDetails.serialNumber),
        biosUuid:
          deviceDetails.biosUuid === '' || !isPCUUID(deviceDetails.biosUuid)
      });
      return;
    }

    if (
      !isSerialNumber(deviceDetails.serialNumber) ||
      !isPCUUID(deviceDetails.biosUuid)
    ) {
      setInputError({
        serialNumber: !isSerialNumber(deviceDetails.serialNumber),
        biosUuid: !isPCUUID(deviceDetails.biosUuid)
      });
      return;
    }

    const payload: PostDeviceTelemetryPayload = { device };
    dispatch(postDeviceTelemetryRequest(payload));
  };

  const {
    psRequestButtonAnalyticsAttributes,
    learnMoreButtonAnalyticsAttributes
  } = useMemo(
    () => ({
      psRequestButtonAnalyticsAttributes: releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `request-button-${deviceDetails.serialNumber || undefined}`,
            'data-gtm-value': `pc-modal-request-data-${deviceDetails.serialNumber || undefined}`
          }
        : {},
      learnMoreButtonAnalyticsAttributes: releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': 'pc-learn-more-button',
            'data-gtm-value': 'pc-modal-learn-more-bios-uuid-and-serial-number'
          }
        : {}
    }),
    [deviceDetails.serialNumber, releasePlatformHpAnalytics]
  );

  return (
    open && (
      <StyledModal
        id={'request-pc-data-modal'}
        align="start"
        closeButton
        show={open}
        onClose={() => {
          setDeviceDetails({ serialNumber: '', biosUuid: '' });
          setInputError({ serialNumber: false, biosUuid: false });

          hasStartedTyping.current = {
            serialNumber: false,
            biosUuid: false
          };
          openModal(false);
        }}
        title={translate('components.request-data-modal.title')}
        alignFooter="end"
        footer={
          <Button
            appearance="secondary"
            onClick={handlePCRequestTelemetryData}
            loading={postDeviceTelemetry.status === OperationStatus.RUNNING}
            data-testid="react-device-management-mfe-request-pc-data-modal-submit-button"
            {...psRequestButtonAnalyticsAttributes}
          >
            {translate('components.request-data-modal.button.request-data')}
          </Button>
        }
        data-testid="react-device-management-mfe-request-pc-data-modal"
        $veneerTheme={theme}
        $id={'request-pc-data-modal'}
      >
        <>
          <PlainButton
            $veneerTheme={theme}
            appearance="ghost"
            onClick={() => {
              window.open(
                URLS.checkSerialNoBiosUuid(country, language),
                '_blank'
              );
            }}
            data-testid="react-device-management-mfe-request-pc-data-modal-learn-more-button"
            {...learnMoreButtonAnalyticsAttributes}
          >
            {translate('components.request-data-modal.button.learn-more')}
          </PlainButton>

          <TextGroup $veneerTheme={theme}>
            <TextBox
              error={inputError.serialNumber}
              helperText={
                inputError.serialNumber &&
                translate(
                  'components.request-data-modal.form.serial-number.error'
                )
              }
              label={translate(
                'components.request-data-modal.form.serial-number.label'
              )}
              placeholder={translate(
                'components.request-data-modal.form.placeholder'
              )}
              separateLabel
              value={deviceDetails.serialNumber}
              onChange={(value) =>
                handleChange(
                  { ...deviceDetails, serialNumber: value },
                  'serialNumber'
                )
              }
              maxLength={10}
              data-testid="react-device-management-mfe-request-pc-data-modal-serial-number-input"
            />
            <TextBox
              error={inputError.biosUuid}
              helperText={
                inputError.biosUuid &&
                translate('components.request-data-modal.form.bios-uuid.error')
              }
              label={translate(
                'components.request-data-modal.form.bios-uuid.label'
              )}
              placeholder={translate(
                'components.request-data-modal.form.placeholder'
              )}
              separateLabel
              value={deviceDetails.biosUuid}
              onChange={(value) =>
                handleChange({ ...deviceDetails, biosUuid: value }, 'biosUuid')
              }
              maxLength={36}
              data-testid="react-device-management-mfe-request-pc-data-modal-bios-uuid-input"
            />
          </TextGroup>
        </>
      </StyledModal>
    )
  );
};

export default RequestDataModal;
